import { useQuery } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";

import { Timestamp } from "../../components/ui/Timestamp";
import { fetchEvidence, type EvidenceItem } from "../../api/evidence";

type Props = {
  callId: string;
  callState: "LIVE" | "ENDED" | "HALTED";
};

const TOOL_LABELS: Record<string, string> = {
  record_credential: "Credentials",
  record_pii: "PII",
  flag_corp_data: "Corp data",
};

function labelFor(toolName: string): string {
  return TOOL_LABELS[toolName] ?? toolName;
}

/**
 * Evidence captured by agent tools (record_credential / record_pii /
 * flag_corp_data) during the call. Fetched from
 * `GET /api/calls/{id}/evidence`, grouped by tool_name.
 *
 * Hidden during LIVE state — evidence is written through the audit-chain
 * pipeline and we don't surface mid-call snapshots; operators see it
 * after the call ends, alongside the recording.
 */
export function EvidencePanel({ callId, callState }: Props) {
  const query = useQuery({
    queryKey: ["call-evidence", callId],
    queryFn: () => fetchEvidence(callId),
    enabled: callState !== "LIVE",
  });

  if (callState === "LIVE") return null;

  const items = query.data ?? [];
  const grouped = groupByTool(items);

  return (
    <div
      data-testid="evidence-panel"
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        overflow: "hidden",
      }}
    >
      {/* Header bar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "11px 18px",
          background: "var(--bg-secondary)",
          borderBottom: "1px solid var(--border-subtle)",
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          textTransform: "uppercase",
          letterSpacing: "0.16em",
          color: "var(--text-muted)",
        }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <ShieldAlert size={11} strokeWidth={1.5} />
          Evidence
        </span>
        <span>
          {query.isPending
            ? "LOADING…"
            : query.isError
              ? "ERROR"
              : `${items.length} ITEM${items.length === 1 ? "" : "S"}`}
        </span>
      </div>

      {/* Body */}
      {query.isPending ? (
        <StatusBody label="Loading evidence…" />
      ) : query.isError ? (
        <ErrorBody
          message={String(query.error)}
          onRetry={() => query.refetch()}
        />
      ) : items.length === 0 ? (
        <StatusBody label="No evidence captured during this call." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {Object.entries(grouped).map(([toolName, rows]) => (
            <ToolGroup key={toolName} toolName={toolName} rows={rows} />
          ))}
        </div>
      )}
    </div>
  );
}

function groupByTool(items: EvidenceItem[]): Record<string, EvidenceItem[]> {
  return items.reduce<Record<string, EvidenceItem[]>>((acc, item) => {
    const k = item.tool_name;
    (acc[k] ??= []).push(item);
    return acc;
  }, {});
}

function ToolGroup({
  toolName,
  rows,
}: {
  toolName: string;
  rows: EvidenceItem[];
}) {
  return (
    <div
      data-testid={`evidence-group-${toolName}`}
      style={{ borderBottom: "1px solid var(--border-subtle)" }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          padding: "10px 18px",
          background: "var(--bg-tertiary)",
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: "var(--text-secondary)",
        }}
      >
        <span>{labelFor(toolName)}</span>
        <span style={{ color: "var(--text-muted)" }}>
          {rows.length} {rows.length === 1 ? "ROW" : "ROWS"}
        </span>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {rows.map((row, i) => (
          <EvidenceRow
            key={row.evidence_id}
            item={row}
            isLast={i === rows.length - 1}
          />
        ))}
      </div>
    </div>
  );
}

function EvidenceRow({
  item,
  isLast,
}: {
  item: EvidenceItem;
  isLast: boolean;
}) {
  const payloadEntries = Object.entries(item.payload);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "180px 1fr",
        gap: 16,
        padding: "12px 18px",
        borderBottom: isLast ? "none" : "1px dashed var(--border-subtle)",
      }}
    >
      {/* Left meta column */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <Timestamp iso={item.captured_at} variant="inline" align="left" />
        {item.turn_index != null ? (
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
            }}
          >
            Turn T{String(item.turn_index).padStart(2, "0")}
          </span>
        ) : null}
      </div>

      {/* Right payload column */}
      {payloadEntries.length === 0 ? (
        <span style={{ color: "var(--text-muted)", fontSize: 12.5 }}>
          (empty payload)
        </span>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {payloadEntries.map(([k, v]) => (
            <div
              key={k}
              style={{
                display: "grid",
                gridTemplateColumns: "140px 1fr",
                gap: 12,
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: "var(--text-muted)",
                  paddingTop: 2,
                }}
              >
                {k}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-sans)",
                  color: "var(--text-primary)",
                  wordBreak: "break-word",
                }}
              >
                {renderValue(v)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function StatusBody({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "28px 24px",
        textAlign: "center",
        color: "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {label}
    </div>
  );
}

function ErrorBody({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      style={{
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 10,
      }}
    >
      <span
        style={{
          color: "var(--error)",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
        }}
      >
        Failed to load evidence
      </span>
      <span
        style={{
          color: "var(--text-muted)",
          fontSize: 12,
          textAlign: "center",
        }}
      >
        {message}
      </span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          marginTop: 4,
          padding: "6px 14px",
          borderRadius: "var(--radius-input)",
          background: "transparent",
          border: "1px solid var(--border-strong)",
          color: "var(--text-primary)",
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </div>
  );
}
